// Test service for Grade 11-12 (Plus1)

import { supabase } from "./supabaseClient";
import type { TestQuestion } from "../types";

export interface TestAnswer {
  question_id: number;
  selected_option_index: number;
  is_correct: boolean;
}

export interface SaveTestResultResponse {
  success: boolean;
  result_id?: number;
  attempt_number?: number;
  is_best_attempt?: boolean;
  previous_best_score?: number;
}

export interface TestResult {
  id: number;
  concept_id: number;
  concept_name: string;
  test_type: string;
  difficulty_level: string;
  score: number;
  total_questions: number;
  percentage: number;
  time_spent_seconds: number;
  attempt_number: number;
  is_best_attempt: boolean;
  submitted_at: string;
}

export interface StudentAnalytics {
  summary: {
    total_tests: number;
    avg_score: number;
    total_time_minutes: number;
    concepts_studied: number;
  };
  by_difficulty: Array<{
    difficulty: string;
    tests_taken: number;
    avg_score: number;
  }>;
  by_subject: Array<{
    subject_id: number;
    subject_name: string;
    tests_taken: number;
    avg_score: number;
  }>;
  weak_concepts: Array<{
    concept_id: number;
    concept_name: string;
    avg_score: number;
    tests_taken: number;
  }>;
  strong_concepts: Array<{
    concept_id: number;
    concept_name: string;
    avg_score: number;
    tests_taken: number;
  }>;
  recent_activity: Array<{
    concept_id: number;
    concept_name: string;
    difficulty: string;
    score: number;
    total: number;
    submitted_at: string;
  }>;
}

/**
 * Save test result for Plus1 students
 */
export async function saveTestResultPlus1(
  studentId: string,
  conceptId: number,
  testType: string,
  difficultyLevel: string,
  score: number,
  totalQuestions: number,
  questionsCorrect: number,
  timeSpentSeconds: number,
  answers: TestAnswer[]
): Promise<SaveTestResultResponse> {
  try {
    const { data, error } = await supabase.rpc('save_test_result_plus1', {
      p_student_id: studentId,
      p_concept_id: conceptId,
      p_test_type: testType,
      p_difficulty_level: difficultyLevel,
      p_score: score,
      p_total_questions: totalQuestions,
      p_questions_correct: questionsCorrect,
      p_time_spent_seconds: timeSpentSeconds,
      p_answers: answers
    });

    if (error) {
      console.error('Error saving test result:', error);
      return { success: false };
    }

    return data as SaveTestResultResponse;
  } catch (error) {
    console.error('Failed to save test result:', error);
    return { success: false };
  }
}

/**
 * Get test history for a student
 */
export async function getTestHistoryPlus1(
  studentId: string,
  conceptId?: number,
  limit: number = 50
): Promise<TestResult[]> {
  try {
    const { data, error } = await supabase.rpc('get_student_test_history_plus1', {
      p_student_id: studentId,
      p_concept_id: conceptId || null,
      p_limit: limit
    });

    if (error) {
      console.error('Error fetching test history:', error);
      return [];
    }

    return (data || []) as TestResult[];
  } catch (error) {
    console.error('Failed to fetch test history:', error);
    return [];
  }
}

/**
 * Get student analytics
 */
export async function getStudentAnalyticsPlus1(
  studentId: string
): Promise<StudentAnalytics | null> {
  try {
    const { data, error } = await supabase.rpc('get_student_analytics_plus1', {
      p_student_id: studentId
    });

    if (error) {
      console.error('Error fetching analytics:', error);
      return null;
    }

    return data as StudentAnalytics;
  } catch (error) {
    console.error('Failed to fetch analytics:', error);
    return null;
  }
}

/**
 * Transform test questions from DB format to UI format
 */
export function transformTestQuestions(questions: Record<string, unknown>[]): TestQuestion[] {
  return questions.map(q => ({
    id: String(q.id),
    question: q.question as string,
    options: [q.option_a as string, q.option_b as string, q.option_c as string, q.option_d as string],
    correctOptionIndex: q.correct_option_index as number
  }));
}
